/* eslint-disable max-len */
import React, {
  useEffect, useState, useRef, useContext,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Tabs, Tab,
} from '@mui/material';
import PropTypes from 'prop-types';
import Table from '../../components/Table';
import TabPanel from '../../components/TabPanel';
import SumBySourceForm from '../../components/SBSForm';
import APIWrapper from '../../utils/graphqlwrapper';
import AppContext from '../../context';
import logger from '../../utils/logger';
import { sumBySourceExportResultsColumns } from '../../utils/schemaInfo';
import { createSumBySourceExportResultsRows } from '../../utils/tableHelpers';

const initColumnVisibilityModel = {
  id: false,
  client_id: true,
  status: true,
  signed_s3_url: true,
  errors: true,
  saved_dt: true,
};

const defaultSortModel = [{ field: 'saved_dt', sort: 'desc' }];

function SBSLanding(props) {
  const { userEmail } = props;
  const [tab, setTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    initColumnVisibilityModel,
  );
  const [pageState, setPageState] = useState({
    isLoading: true,
    page: 0, // mui pages are 0 indexed
    pageSize: 10,
    data: [],
    total: 0,
  });
  const hasFetchedData = useRef(false);
  const { setShouldLogOut, authenticated } = useContext(AppContext);
  const [sortModel, setSortModel] = useState(defaultSortModel);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [currentExecutionId, setCurrentExecutionId] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null); // eslint-disable-line no-unused-vars
  const { client } = useParams();

  const columns = sumBySourceExportResultsColumns;
  const getDefaultColumns = () => columns;
  const twelveHoursAgo = new Date(Date.now() - 12 * 60 * 60 * 1000); // -12 hours from now

  useEffect(() => {
    document.title = 'MissionPortal | Sum by Source';
  }, []);

  // handle tab changes
  const handleChange = (event, newValue) => {
    setTab(newValue);
    setSearchParams({ tab: newValue });
  };

  // Function to fetch recent executions (extracted for reuse)
  const fetchRecentExecutions = async () => {
    /**
     * Fetches recent executions for the current client to display in a table
     */
    try {
      setPageState((old) => ({ ...old, isLoading: true }));
      const result = await APIWrapper.queryApi(
        { query: 'sumBySourceExportResultsByClientID' },
        setShouldLogOut,
        {
          client_id: client,
          filter: { saved_dt: { ge: twelveHoursAgo.toISOString() } },
          limit: 100,
          sortDirection: 'DESC',
        },
      )
        .then((data) => data)
        .catch((e) => {
          logger.error(e);
        });
      const formedRows = createSumBySourceExportResultsRows(result.data);
      setPageState((old) => ({
        ...old,
        isLoading: false,
        data: formedRows,
        total: formedRows.length,
      }));
    } catch (error) {
      logger.error('Error fetching recent executions:', error);
      setPageState((old) => ({ ...old, isLoading: false }));
    }
  };

  // Fetch recent executions when user navigates to the "View all downloads" tab
  useEffect(() => {
    if (!hasFetchedData.current && tab === 1 && authenticated && client) {
      fetchRecentExecutions()
        .then(() => {
          hasFetchedData.current = true;
        })
        .catch((e) => {
          logger.log(e);
        });
    }
  }, [tab, authenticated, client]);

  // handle tab from URL params
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      setTab(parseInt(tabParam, 10));
    }
  }, [searchParams]);

  // Listen for status updates from the background service
  useEffect(() => {
    const handleStatusUpdate = (event) => {
      /**
         * Handles status updates from the background subscription service
         * @param {CustomEvent} event - The event containing status update details
         */
      const { executionId, clientId, data } = event.detail;

      // Only update if it's for the current client
      if (clientId === client) {
        logger.info('Received status update for client:', { clientId, executionId, status: data.status });

        // Update execution status if it's for the current execution
        if (executionId === currentExecutionId) {
          // update Alert in SBSForm
          setExecutionStatus(data.status);
        }

        // Refresh the table data to show the latest results
        fetchRecentExecutions()
          .then(() => {
            logger.info('Table data refreshed after subscription update');
          })
          .catch((error) => {
            logger.error('Error refreshing table data after subscription update:', error);
          });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('sbsStatusUpdate', handleStatusUpdate);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('sbsStatusUpdate', handleStatusUpdate);
      }
    };
  }, [currentExecutionId]);

  return (
    <div className="App">
      <header className="App-header">
        <h2>Sum by Source/Category Download</h2>
      </header>
      <Box sx={{ width: '100%' }}>
        <>
          {' '}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={handleChange}>
              <Tab label="Generate Report" />
              <Tab label="View all downloads" />
            </Tabs>
          </Box>
          <>
            <TabPanel label="Generate Report" value={tab} index={0}>
              <SumBySourceForm setCurrentExecutionId={setCurrentExecutionId} currentExecutionId={currentExecutionId} executionStatus={executionStatus} userEmail={userEmail} />
            </TabPanel>
            <TabPanel label="View all downloads" value={tab} index={1}>
              <Box sx={{ flexGrow: 1 }}>
                <Table
                  pageState={pageState}
                  updatePageState={setPageState}
                  columns={columns}
                  sortModel={sortModel}
                  setSortModel={setSortModel}
                  columnVisibilityModel={columnVisibilityModel}
                  setColumnVisibilityModel={setColumnVisibilityModel}
                  filterModel={filterModel}
                  setFilterModel={setFilterModel}
                  tablePrefix="sbsexportresults"
                  getDefaultColumns={getDefaultColumns}
                />
              </Box>
            </TabPanel>
          </>
        </>
      </Box>
    </div>
  );
}

export default SBSLanding;

SBSLanding.propTypes = {
  userEmail: PropTypes.string,
};
